/**
 * Citizens Information Brand Configuration
 * 
 * Brand-specific settings for the AskCI chatbot application
 * when running under the Citizens Information brand.
 */

import type { ConversationAccessibilityLocale } from '../localization-types';

const citizensInfoIrishConversationAccessibility = {
  actions: {
    buttons: {
      like: 'Is maith liom an freagra seo',
      dislike: 'Ní maith liom an freagra seo',
      copy: 'Cóipeáil an freagra',
      viewThoughtProcess: 'Féach ar an bpróiseas smaointe',
      playAudio: 'Seinn fuaim',
      stopAudio: 'Stop fuaim',
      thisAnswer: 'an freagra seo',
      moreActions: 'Tuilleadh gníomhartha',
      closeDialog: 'Dún'
    },
    menu: {
      exportMarkdown: 'Easpórtáil - Markdown',
      exportText: 'Easpórtáil - Téacs',
      report: 'Tuairiscigh'
    },
    reportDialog: {
      title: 'Tuairiscigh Teachtaireacht',
      description: 'Úsáid an fhoirm seo chun aiseolas a thabhairt nó fadhb leis an bhfreagra seo a thuairisciú.',
      reasonLabel: 'Cúis',
      detailsLabel: 'Sonraí (Roghnach)',
      cancelButton: 'Cealaigh',
      submitButton: 'Cuir isteach'
    },
    toasts: {
      feedbackSubmittedLike: 'Cuireadh d’aiseolas isteach (is maith liom).',
      feedbackSubmittedDislike: 'Cuireadh d’aiseolas isteach (ní maith liom).',
      errorSendingLike: 'Earráid agus aiseolas dearfach á sheoladh: ',
      errorSendingDislike: 'Earráid agus aiseolas diúltach á sheoladh: ',
      answerCopied: 'Freagra cóipeáilte chuig an ngearrthaisce!',
      answerCopiedMarkdown: 'Freagra cóipeáilte mar Markdown chuig an ngearrthaisce!',
      answerExportedText: 'Freagra easpórtáilte mar chomhad téacs!',
      reportSubmitted: 'Tuairisc curtha isteach go rathúil.',
      errorSubmittingReport: 'Earráid agus tuairisc á cur isteach: ',
      playingAudio: 'Fuaim á seinm...',
      failedToSynthesize: 'Theip ar shintéisiú cainte: '
    },
    labels: {
      eventHistory: 'Stair Imeachtaí:'
    }
  },
  citations: {
    viewSourcePrefix: 'Foinse: ',
    citationUtmParams: 'utm_source=askci-app&utm_medium=chatbot&utm_campaign=citation'
  },
  screenReader: {
    userAsks: 'Iarrann an t-úsáideoir:',
    processing: 'Tá freagra á lorg ag AskCI...',
    streaming: 'Tá freagra á chlóscríobh ag AskCI...',
    responseSays: 'Deir AskCI:',
    withSources: 'le {count} foinsí tagartha',
    withFollowUps: '{count} ceisteanna leantacha atá ar fáil',
    responseComplete: 'Freagra tugtha.'
  }
} satisfies ConversationAccessibilityLocale;

export const citizensInfoBrandConfig = {
  // Application branding
  defaultLanguage: 'en' as const,
  appName: 'AskCI',
  appTitle: 'AskCI - Citizens Information',
  appDescription: 'An AI tool to help you explore citizensinformation.ie',
  websiteLink: 'https://www.citizensinformation.ie/',
  
  // Title formatting - allows brand-specific styling without hardcoding in components
  titleFormatting: {
    // Parts of the app name for styled display (e.g., "Ask" + bold "CI")
    appNameParts: [
      { text: 'Ask', bold: false },
      { text: 'CI', bold: true }
    ],
    // Parts of the hero title for styled display
    heroTitleParts: [
      { text: 'Welcome to Ask', bold: false },
      { text: 'CI', bold: true }
    ]
  },
  
  // Header configuration
  header: {
    showLogo: true,
    logoVariant: 'white' as const,
    showAppName: true,
    navigationStyle: 'modern' as const, // 'government' | 'modern' | 'minimal'
    logoWidth: 175,
    voiceSettings: {
      buttonTitle: 'Voice Settings',
      voiceSelectionLabel: 'Voice Selection',
      briefLabel: 'Brief Response',
      prefetchLabel: 'Prefetch speech audio',
      speedLabel: 'Playback speed',
      speedOptions: {
        normal: 'Normal',
        faster: 'Faster'
      },
      selectedPrefix: 'Selected: ',
      defaultVoice: 'Default Voice'
    },
    languageSwitcher: {
      groupLabel: 'Language switcher',
      englishLabel: 'English',
      irishLabel: 'Irish',
      englishShortLabel: 'EN',
      irishShortLabel: 'GA',
      englishAriaLabel: 'Switch language to English',
      irishAriaLabel: 'Switch language to Irish',
    }
  },
  
  // Color scheme - Official CitizensInfo Palette
  colors: {
    primary: 'var(--brand-60)',      // CitizensInfo Blue
    secondary: 'var(--brand-accent-60)',    // CitizensInfo Green
    accent: 'var(--brand-green)',       // CitizensInfo Green Accent
    header: {
      background: 'var(--brand-60)',
      text: 'var(--neutral-0)',
      brandBackground: 'var(--brand-accent-60)',
      brandText: 'var(--neutral-100)', // Dark text for contrast on green background
    }
  },
  
  // Design system
  design: {
    borderRadius: 'rounded', // 'sharp' | 'rounded' | 'mixed'
    borderStyle: 'soft', // 'angular' | 'soft' | 'mixed'
    shadowStyle: 'subtle', // 'subtle' | 'prominent' | 'minimal'
    typography: 'modern', // 'government' | 'modern' | 'classic'
  },
  
  // Layout preferences
  layout: {
    headerHeight: '56px',
    contentPadding: 'var(--space-4)',
    cardSpacing: 'var(--space-4)',
    footerStyle: 'ghost' as const, // 'ghost' | 'default'
    hideBottomInputOnWelcome: true, // Hide bottom chat input on welcome screen (same as Údarás)
    chatInputContainerStyle: 'default' as const, // 'floating' | 'default'
    backgroundOverlay: 'linear-gradient(rgba(0, 0, 0, 0.40), rgba(0, 0, 0, 0.10))', // Dark overlay for better text contrast
  },
  
  // Feature flags
  features: {
    showLanguageSwitcher: true,
    enableVoiceInput: true,
    enableVoiceSynthesis: true,
    speechProvider: 'openai' as const,
    showVoiceSettings: false,
    synthesisVoice: 'en-IE-EmilyNeural',
    synthesisVoices: {
      en: 'en-IE-EmilyNeural',
      ga: 'ga-IE-OrlaNeural',
    },
    enableBriefResponse: false,
    enableMarkdownExport: false, // Disabled - use text export instead
  },
  
  // Footer configuration
  footer: {
    // Left section: Privacy statement link
    leftText: 'Privacy Statement',
    leftLink: 'https://www.citizensinformation.ie/en/about/privacy/',
    
    // Center section: Brand copyright
    centerText: 'Citizens Information Board',
    centerLink: 'https://www.citizensinformationboard.ie/en/',
    
    // Right section: About link with version (APP_VERSION is always appended)
    rightText: 'About: AskCI - ',
    rightLink: 'https://www.citizensinformation.ie/en/about/askci/',
  },
  
  // Hero section content
  hero: {
    title: 'Welcome to AskCI',
    subtitle: 'An AI tool to help you explore citizensinformation.ie',
    tryAskingLabel: 'Try asking:',
    showInputField: true, // Show input field on welcome screen
    inputFieldPosition: 'bottom' as const, // 'top' | 'bottom' - position relative to suggested questions
    suggestedQuestions: [
      'What is auto-enrolment?',
      'Can I get help with low pay?',
      'How much is Maternity Benefit?'
    ],
    disclaimerText: 'AskCI uses AI and can make mistakes. Check important information and contact a ',
    disclaimerLinkText: 'Citizens Information Centre',
    disclaimerLinkUrl: 'https://centres.citizensinformation.ie/',
    disclaimerAfterLinkText: ' or call the Citizens Information Phone Service on {phoneNumber} for advice on your situation. AskCI is currently in a pilot phase.',
    disclaimerPhoneNumber: '0818 07 4000'
  },
  
  // Chat input
  chatInput: {
    placeholder: 'Ask a question by speaking or typing...',
    ariaLabel: 'Ask a question by speaking or typing...',
    clearButton: 'Clear',
    sendButton: 'Send',
    voiceInput: {
      preparingPlaceholder: 'Preparing microphone...',
      listeningPlaceholder: 'Listening... Speak now.',
      processingPlaceholder: 'Processing voice input...',
      preparingStatus: 'Preparing...',
      listeningStatus: 'Speak now',
      connectingAriaLabel: 'Voice input. Alt plus M starts or stops recording; Escape cancels.',
      stopRecordingAriaLabel: 'Stop recording',
      processingAriaLabel: 'Processing voice input',
      startRecordingAriaLabel: 'Voice input. Alt plus M starts or stops recording; Escape cancels.',
      cancelRecordingAriaLabel: 'Cancel recording',
      cancelRecordingTitle: 'Cancel recording',
      clearInputAriaLabel: 'Clear input text',
      clearInputTitle: 'Clear text',
      unsupportedBrowserMessage: 'Voice input is not available in this browser. You can still type your question.',
      recordingAnnouncement: 'Recording',
      recordingCancelledAnnouncement: 'Recording cancelled',
      silenceDetectedAnnouncement: 'Silence detected. Recording stopped.',
      processingVoiceInputLabel: 'Processing voice input',
      recordingTooShortMessage: 'Recording too short. Please try again.',
      recordingTooShortAnnouncement: 'Recording was too short',
      transcriptionResultPrefix: 'This is what we heard:',
      noSpeechDetectedMessage: 'No speech detected. Please try again.',
      noSpeechDetectedAnnouncement: 'No speech detected',
      transcriptionFailedMessage: 'Transcription failed',
      errorPrefix: 'Error:',
      recordingFailedMessage: 'Recording failed. Please try again.',
      microphoneAccessDeniedMessage: 'Microphone access denied. Please allow it in browser settings.',
      noMicrophoneDetectedMessage: 'No microphone detected.',
      microphoneAccessFailedMessage: 'Could not access microphone.'
    },
    clearConfirmDialog: {
      title: 'Clear conversation?',
      description: 'This will delete your current conversation session. This cannot be undone.',
      cancelButton: 'Cancel',
      confirmButton: 'Clear'
    }
  },
  
  // Answer actions
  actions: {
    buttons: {
      like: 'Like this answer',
      dislike: 'Dislike this answer',
      copy: 'Copy answer',
      viewThoughtProcess: 'View thought process',
      playAudio: 'Play audio',
      stopAudio: 'Stop audio',
      thisAnswer: 'this answer',
      moreActions: 'More actions',
      closeDialog: 'Close'
    },
    menu: {
      exportMarkdown: 'Export - Markdown',
      exportText: 'Export - Text',
      report: 'Report'
    },
    reportDialog: {
      title: 'Report Message',
      description: 'Use this form to provide feedback or report an issue with this answer.',
      reasonLabel: 'Reason',
      detailsLabel: 'Details (Optional)',
      cancelButton: 'Cancel',
      submitButton: 'Submit'
    },
    toasts: {
      feedbackSubmittedLike: 'Feedback submitted (like).',
      feedbackSubmittedDislike: 'Feedback submitted (dislike).',
      errorSendingLike: 'Error sending like feedback: ',
      errorSendingDislike: 'Error sending dislike feedback: ',
      answerCopied: 'Answer copied to clipboard!',
      answerCopiedMarkdown: 'Answer copied as Markdown to clipboard!',
      answerExportedText: 'Answer exported as text file!',
      reportSubmitted: 'Report submitted successfully.',
      errorSubmittingReport: 'Error submitting report: ',
      playingAudio: 'Playing audio...',
      failedToSynthesize: 'Failed to synthesize speech: '
    },
    labels: {
      eventHistory: 'Event History:'
    }
  },
  
  // Messages
  messages: {
    processing: 'Processing:',
    initializing: 'Initializing...',
    userMessageLabel: 'Your question',
    botResponseLabel: 'Answer from AskCI'
  },
  
  // Events
  events: {
    buttonTitle: 'Events',
    historyLabel: 'Event History:'
  },
  
  // Follow-up
  followUp: {
    ariaLabelPrefix: 'Ask: '
  },
  
  // Citations
  citations: {
    viewSourcePrefix: 'View source: ',
    // UTM tracking for analytics - appended to all citation URLs
    citationUtmParams: 'utm_source=askci-app&utm_medium=chatbot&utm_campaign=citation'
  },
  
  // Accessibility settings
  accessibility: {
    skipLinkText: 'Skip to main content',
    homeLabel: 'Home',
    opensInNewTab: 'opens in new tab',
    versionLabel: 'Version',
  },
  
  // Screen reader announcements (WCAG 2.1 Level AA)
  screenReader: {
    userAsks: 'User asks:',
    processing: 'AskCI is searching for an answer...',
    streaming: 'AskCI is typing a response...',
    responseSays: 'AskCI says:',
    withSources: 'with {count} source references',
    withFollowUps: '{count} follow-up questions available',
    responseComplete: 'Response complete.',
  },
  
  // Rate limiting (WCAG 2.2 AAA compliant)
  rateLimiting: {
    friendly: {
      title: 'Please slow down',
      message: 'You\'re asking questions too quickly. Please wait a moment before trying again. This helps us provide quality responses to everyone.',
      countdown: 'You can send another message in {seconds} seconds',
      screenReaderAnnouncement: 'Rate limit reached. Please wait {seconds} seconds before sending another message.',
      extensionButton: 'Request additional time'
    },
    warning: {
      message: 'You\'re sending questions very quickly. Please slow down to avoid being temporarily limited.',
      threshold: 0.8  // Warn at 80% of limit
    },
    accessibility: {
      errorRole: 'alert' as const,
      livePriority: 'assertive' as const,
      countdownRole: 'timer' as const
    }
  },
  
  // Citizens Information-specific settings
  citizensInfo: {
    complianceMode: false,
    accessibilityLevel: 'WCAG-AA',
    dataRetention: '3years',
  },

  locales: {
    ga: {
      ...citizensInfoIrishConversationAccessibility,
      appDescription: 'Uirlis AI chun cabhrú leat iniúchadh a dhéanamh ar citizensinformation.ie',
      titleFormatting: {
        heroTitleParts: [
          { text: 'Fáilte go Ask', bold: false },
          { text: 'CI', bold: true }
        ]
      },
      header: {
        voiceSettings: {
          buttonTitle: 'Socruithe Gutha',
          voiceSelectionLabel: 'Roghnú Gutha',
          briefLabel: 'Freagra Gearr',
          prefetchLabel: 'Réamhshintéisiú fuaime',
          speedLabel: 'Luas athsheinm',
          speedOptions: {
            normal: 'Gnáthluas',
            faster: 'Tapúla'
          },
          selectedPrefix: 'Roghnaithe: ',
          defaultVoice: 'Guth Réamhshocraithe'
        },
        languageSwitcher: {
          groupLabel: 'Roghnaitheoir teanga',
          englishLabel: 'Béarla',
          irishLabel: 'Gaeilge',
          englishAriaLabel: 'Athraigh an teanga go Béarla',
          irishAriaLabel: 'Athraigh an teanga go Gaeilge'
        }
      },
      footer: {
        leftText: 'Ráiteas Príobháideachta',
        centerText: 'An Bord um Fhaisnéis do Shaoránaigh',
        rightText: 'Maidir le: AskCI - '
      },
      hero: {
        title: 'Fáilte go AskCI',
        subtitle: 'Uirlis AI chun cabhrú leat iniúchadh a dhéanamh ar citizensinformation.ie',
        tryAskingLabel: 'Bain triail as na ceisteanna seo:',
        suggestedQuestions: [
          'Cad is uathrollú ann?',
          'An bhféadfaidh mé cabhair a fháil le pá íseal?',
          'Cén méid atá sa Sochar Máithreachais?'
        ],
        disclaimerText: 'Úsáideann AskCI AI agus is féidir leis botúin a dhéanamh. Seiceáil eolas tábhachtach agus déan teagmháil le ',
        disclaimerLinkText: 'Lárionad Faisnéise do Shaoránaigh',
        disclaimerAfterLinkText: ' nó cuir glaoch ar an tSeirbhís Teileafóin um Fhaisnéis do Shaoránaigh ar {phoneNumber} chun comhairle a fháil faoi do chás. Tá AskCI i gcéim phíolótach faoi láthair.'
      },
      chatInput: {
        placeholder: 'Labhair liom nó clóscríobh anseo chun ceist a chur orm...',
        ariaLabel: 'Labhair liom nó clóscríobh anseo chun ceist a chur orm...',
        clearButton: 'Glan',
        sendButton: 'Seol',
        voiceInput: {
          preparingPlaceholder: 'Ag ullmhú an mhicreafóin...',
          listeningPlaceholder: 'Ag éisteacht... Labhair anois.',
          processingPlaceholder: 'Ag próiseáil ionchur gutha...',
          preparingStatus: 'Ag ullmhú...',
          listeningStatus: 'Labhair anois',
          connectingAriaLabel: 'Ionchur gutha. Tosaíonn nó stopann Alt agus M an taifeadadh; cuireann Escape ar ceal é.',
          stopRecordingAriaLabel: 'Stop an taifeadadh',
          processingAriaLabel: 'Ag próiseáil ionchur gutha',
          startRecordingAriaLabel: 'Ionchur gutha. Tosaíonn nó stopann Alt agus M an taifeadadh; cuireann Escape ar ceal é.',
          cancelRecordingAriaLabel: 'Cealaigh taifeadadh',
          cancelRecordingTitle: 'Cealaigh taifeadadh',
          clearInputAriaLabel: 'Glan téacs ionchuir',
          clearInputTitle: 'Glan téacs',
          unsupportedBrowserMessage: 'Níl ionchur gutha ar fáil sa bhrabhsálaí seo. Is féidir leat do cheist a chlóscríobh fós.',
          recordingAnnouncement: 'Taifeadadh',
          recordingCancelledAnnouncement: 'Cuireadh an taifeadadh ar ceal',
          silenceDetectedAnnouncement: 'Braitheadh ciúnas. Stopadh an taifeadadh.',
          processingVoiceInputLabel: 'Ag próiseáil ionchur gutha',
          recordingTooShortMessage: 'Tá an taifeadadh ró-ghearr. Bain triail eile as.',
          recordingTooShortAnnouncement: 'Bhí an taifeadadh ró-ghearr',
          transcriptionResultPrefix: 'Seo a chualamar:',
          noSpeechDetectedMessage: 'Níor braitheadh aon chaint. Bain triail eile as.',
          noSpeechDetectedAnnouncement: 'Níor braitheadh aon chaint',
          transcriptionFailedMessage: 'Theip ar an tras-scríobh',
          errorPrefix: 'Earráid:',
          recordingFailedMessage: 'Theip ar an taifeadadh. Bain triail eile as.',
          microphoneAccessDeniedMessage: 'Diúltaíodh rochtain ar an micreafón. Ceadaigh é i socruithe an bhrabhsálaí.',
          noMicrophoneDetectedMessage: 'Níor aimsíodh micreafón.',
          microphoneAccessFailedMessage: 'Níorbh fhéidir rochtain a fháil ar an micreafón.'
        },
        clearConfirmDialog: {
          title: 'Glan an comhrá?',
          description: 'Scriosfaidh sé seo do sheisiún comhrá reatha. Ní féidir an gníomh seo a chealú.',
          cancelButton: 'Cealaigh',
          confirmButton: 'Glan'
        }
      },
      messages: {
        processing: 'Ag próiseáil:',
        initializing: 'Ag tosú...',
        userMessageLabel: 'Do cheist',
        botResponseLabel: 'Freagra ó AskCI'
      },
      events: {
        buttonTitle: 'Imeachtaí',
        historyLabel: 'Stair Imeachtaí:'
      },
      followUp: {
        ariaLabelPrefix: 'Fiafraigh: '
      },
      accessibility: {
        skipLinkText: 'Scipeáil go dtí an príomhábhar',
        homeLabel: 'Baile',
        opensInNewTab: 'osclaítear i gcluaisín nua',
        versionLabel: 'Leagan'
      },
      rateLimiting: {
        friendly: {
          title: 'Moilligh, le do thoil',
          message: 'Tá ceisteanna seolta agat róthapa. Fan nóiméad, le do thoil, sula mbainfidh tú triail eile as.',
          countdown: 'Is féidir teachtaireacht eile a sheoladh i gceann {seconds} soicind',
          screenReaderAnnouncement: 'Teorainn ráta bainte amach. Fan {seconds} soicind sula seolann tú teachtaireacht eile.',
          extensionButton: 'Iarr am breise'
        },
        warning: {
          message: 'Tá ceisteanna seolta agat go han-tapa. Moilligh chun teorannú sealadach a sheachaint.'
        }
      }
    }
  }
} as const;

export type CitizensInfoBrandConfig = typeof citizensInfoBrandConfig;
